export type ArmorSlot = 0 | 1 | 2 | 3; // helmet, chestplate, leggings, boots

export interface ItemDef {
  id: string;
  name: string;
  kind: "sword" | "armor" | "food" | "sign" | "compass" | "bow" | "arrow" | "rod";
  damage?: number;
  slot?: ArmorSlot;
  armor?: number;
  heal?: number;
  color: string;
  stack: number;
}

const MATERIALS = [
  { m: "wood", name: "Wooden", color: "#9c7a48", sword: 4, armorName: "Leather", armorColor: "#a0653a", pts: [1, 3, 2, 1] },
  { m: "gold", name: "Golden", color: "#f6d64a", sword: 4, armorName: "Golden", armorColor: "#f6d64a", pts: [2, 5, 3, 1] },
  { m: "stone", name: "Stone", color: "#8a8a8a", sword: 5, armorName: "Chain", armorColor: "#9aa0a6", pts: [2, 5, 4, 1] },
  { m: "iron", name: "Iron", color: "#dcdcdc", sword: 6, armorName: "Iron", armorColor: "#e6e6e6", pts: [2, 6, 5, 2] },
  { m: "diamond", name: "Diamond", color: "#4ee8de", sword: 7, armorName: "Diamond", armorColor: "#4ee8de", pts: [3, 8, 6, 3] },
];
const ARMOR_PIECES = ["Helmet", "Chestplate", "Leggings", "Boots"];

export const ITEMS: Record<string, ItemDef> = {};
for (const mat of MATERIALS) {
  ITEMS[`${mat.m}_sword`] = { id: `${mat.m}_sword`, name: `${mat.name} Sword`, kind: "sword", damage: mat.sword, color: mat.color, stack: 1 };
  ARMOR_PIECES.forEach((piece, i) => {
    const id = `${mat.m}_${piece.toLowerCase()}`;
    ITEMS[id] = { id, name: `${mat.armorName} ${piece}`, kind: "armor", slot: i as ArmorSlot, armor: mat.pts[i], color: mat.armorColor, stack: 1 };
  });
}
ITEMS.apple = { id: "apple", name: "Apple", kind: "food", heal: 4, color: "#e8322b", stack: 16 };
ITEMS.bread = { id: "bread", name: "Bread", kind: "food", heal: 5, color: "#c8913c", stack: 16 };
ITEMS.steak = { id: "steak", name: "Steak", kind: "food", heal: 8, color: "#8b4a2b", stack: 16 };
ITEMS.golden_apple = { id: "golden_apple", name: "Golden Apple", kind: "food", heal: 12, color: "#f6d64a", stack: 16 };
ITEMS.sign = { id: "sign", name: "Sign", kind: "sign", color: "#b8945f", stack: 16 };
ITEMS.compass = { id: "compass", name: "Compass", kind: "compass", color: "#8e8e8e", stack: 1 };
ITEMS.bow = { id: "bow", name: "Bow", kind: "bow", color: "#8f6a35", stack: 1 };
ITEMS.arrow = { id: "arrow", name: "Arrow", kind: "arrow", color: "#b4b4b4", stack: 32 };
ITEMS.fishing_rod = { id: "fishing_rod", name: "Fishing Rod", kind: "rod", color: "#8f6a35", stack: 1 };

export const FIST_DAMAGE = 1;
/** Pocket Edition 0.12 hotbar size. The last slot holds your signs. */
export const HOTBAR = 8;
/** Everyone spawns with these, they never drop on death and never turn up in chests. */
export const KIT_ITEMS = new Set(["sign", "compass"]);

export type Stack = { id: string; n: number } | null;

// Weighted loot table, tuned so the middle is a little better than the woods.
const LOOT: [string, number][] = [
  ["wood_sword", 14], ["stone_sword", 10], ["gold_sword", 6], ["iron_sword", 4], ["diamond_sword", 1],
  ["bow", 4], ["arrow", 12], ["fishing_rod", 4],
  ["apple", 14], ["bread", 12], ["steak", 7], ["golden_apple", 1.5],
  ["wood_helmet", 6], ["wood_chestplate", 5], ["wood_leggings", 5], ["wood_boots", 6],
  ["gold_helmet", 4], ["gold_chestplate", 3], ["gold_leggings", 3], ["gold_boots", 4],
  ["stone_helmet", 3], ["stone_chestplate", 2], ["stone_leggings", 2], ["stone_boots", 3],
  ["iron_helmet", 2], ["iron_chestplate", 1.2], ["iron_leggings", 1.4], ["iron_boots", 2],
  ["diamond_helmet", 0.4], ["diamond_chestplate", 0.25], ["diamond_leggings", 0.3], ["diamond_boots", 0.4],
];
const LOOT_TOTAL = LOOT.reduce((s, [, w]) => s + w, 0);

export function rollLoot(rand: () => number, rolls: number): Stack[] {
  const out: Stack[] = [];
  for (let i = 0; i < rolls; i++) {
    let r = rand() * LOOT_TOTAL;
    for (const [id, w] of LOOT) {
      r -= w;
      if (r <= 0) {
        const def = ITEMS[id]!;
        const n = def.kind === "food" ? 1 + Math.floor(rand() * 3) : def.kind === "arrow" ? 2 + Math.floor(rand() * 4) : 1;
        const existing = out.find((s) => s && s.id === id && def.stack > 1);
        if (existing) existing.n += n;
        else out.push({ id, n });
        break;
      }
    }
  }
  return out;
}

export function armorPoints(armor: (string | null)[]): number {
  return armor.reduce((s, id) => s + (id ? ITEMS[id]?.armor ?? 0 : 0), 0);
}

// Classic pre-1.9 formula: each armor point blocks 4%.
export function damageAfterArmor(dmg: number, armor: (string | null)[]): number {
  return dmg * (1 - Math.min(20, armorPoints(armor)) * 0.04);
}
